import { Box } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { m } from 'framer-motion'

import Illus from './Illus'

// ----------------------------------------------------------------------
// The boot and page loader.
//
// **The loader draws the car; the chrome draws the tile.** Both used to be
// the same mark, and two marks that both say Beast in the same second read as
// one thing stuttering rather than as an identity and a state. The tile is
// who this is, and it belongs in the sidebar where it does not move; this is
// the thing that is moving, so it is the car — the mark the console drew
// before the tile replaced it, recovered rather than redrawn.
//
// It is illustration tier and not brand tier, and the token source settles
// that itself: `brand.usedFor` says the brand tier is "used where the subject
// is the company, never for a thing that company made: a car sourced from
// Tesla is still a car". `illustration.usedFor` is "the places where art
// leads and there is nothing to operate", which is a loading screen exactly.
//
// The colour is the point of the change and must not be dropped. The mark
// this recovers was a hard-coded `#000000`, which measured 1.24:1 against the
// dark sidebar — a mark nobody could see for half the day, in a value the
// theme had no say over. `Illus` renders `fill="currentColor"`, so stating
// `text.primary` gives 18.1:1 in light and 16.8:1 in dark. It is stated
// rather than inherited for the reason `TeslaMark` records: a `currentColor`
// mark picks up whatever the container was authored for, and a container
// authored for muted icons renders a mark in placeholder grey.
//
// It carries no title, so it is decorative and contributes no accessible
// name. `LoadingScreen` already announces the state with `role="status"`, and
// two screens query `getByRole('img', { name: 'Beast' })` in the singular.
// ----------------------------------------------------------------------

const LoaderAnimation = () => {
  return (
    <>
      <m.div
        initial={{ rotateY: 0 }}
        animate={{ rotateY: 360 }}
        transition={{
          duration: 2,
          ease: 'easeInOut',
          repeatDelay: 1,
          repeat: Infinity,
        }}
      >
        <Illus name="car-front" size="outcome" sx={{ color: 'text.primary' }} />
      </m.div>

      <Box
        component={m.div}
        animate={{
          scale: [1.2, 1, 1, 1.2, 1.2],
          rotate: [270, 0, 0, 270, 270],
          opacity: [0.25, 1, 1, 1, 0.25],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{ ease: 'linear', duration: 3.2, repeat: Infinity }}
        sx={{
          width: 100,
          height: 100,
          borderRadius: '25%',
          position: 'absolute',
          border: (theme) =>
            `solid 3px ${alpha(theme.palette.primary.dark, 0.24)}`,
        }}
      />

      <Box
        component={m.div}
        animate={{
          scale: [1, 1.2, 1.2, 1, 1],
          rotate: [0, 270, 270, 0, 0],
          opacity: [1, 0.25, 0.25, 0.25, 1],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{
          ease: 'linear',
          duration: 3.2,
          repeat: Infinity,
        }}
        sx={{
          width: 120,
          height: 120,
          borderRadius: '25%',
          position: 'absolute',
          border: (theme) =>
            `solid 8px ${alpha(theme.palette.primary.dark, 0.24)}`,
        }}
      />
    </>
  )
}

export default LoaderAnimation
